import { recurrenceLabel } from '@modules/form-controls/types/recurrence-presets';
import { headerNamesZone, zoneShortName } from '@modules/form-controls/types/timezones';
import { isValidTimeZone } from '@sharedlib/common/utils/timezone';

export interface EventHeaderWhenSource {
  startTime?: Date | null;
  endTime?: Date | null;
  allDay?: boolean | null;
  recurrence?: string | null;
  timeZone?: string | null;
}

/**
 * The line under an event's title (stitch-app-shell `detailSub`, Johanna 2026-10-21): when it happens and how it
 * repeats, e.g. `"October 21, 11:00 AM – 12:00 PM · Repeats weekly on Tuesday"`.
 *
 * Times read in `displayZone`, the account's zone the cards and the calendar show (Johanna 2026-10-22: the header
 * must match the card, not the event's own zone that Details edits in). That zone's short name from `zoneShortName`
 * follows only when the event's own zone or the account zone reads a different clock from the browser's at either end
 * (`"October 21, 11:00 AM – 12:00 PM GMT+9"`; Johanna 2026-10-23, `headerNamesZone`: no zone when all three match);
 * across a daylight-saving change each end is named (`"November 1, 1:00 AM EDT – 1:30 AM EST"`), even when all three
 * match, so a range through the repeated hour can't read as ending before it starts.
 * Without a `displayZone` they read in the event's own zone, unnamed. Each time is written in full with its own AM/PM, like the task header's "9:15 AM GMT+9" (Johanna,
 * 2026-10-22: no "11 AM – 12 PM" or "12 – 1 AM"); a day is repeated only when the end falls on another day, and the
 * year is written only when the end falls in another year. An
 * all-day event is a run of floating dates (already at browser-local midnight), so it names no zone:
 * `"October 21 · All day"`, `"October 21 – 23 · All day"`. Returns `''` without a start.
 */
export function eventHeaderWhen(
  source: EventHeaderWhenSource,
  locale = 'en-US',
  displayZone?: string | null,
  browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  const { startTime, allDay, recurrence } = source;
  if (!startTime || isNaN(startTime.getTime())) {
    return '';
  }
  const endTime = source.endTime && !isNaN(source.endTime.getTime()) ? source.endTime : startTime;
  const parts: string[] = [];

  if (allDay) {
    const days = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' });
    parts.push(endTime > startTime ? days.formatRange(startTime, endTime) : days.format(startTime), 'All day');
  } else {
    const display = isValidTimeZone(displayZone);
    const timeZone = display ? displayZone! : isValidTimeZone(source.timeZone) ? source.timeZone! : undefined;
    const ends = endTime > startTime;
    // An end before the start (mid-edit) is no end: the header reads the start alone.
    const last = ends ? endTime : startTime;
    // Either end may be where a clock other than the browser's shows (London on BST in July, on GMT in January).
    // A range across a daylight-saving change is named even when every clock matches: New York's 1:30 AM EDT –
    // 1:15 AM EST would otherwise read "1:30 AM – 1:15 AM", a 45-minute event ending before it starts.
    const named =
      display &&
      (zoneShortName(displayZone!, startTime) !== zoneShortName(displayZone!, last) ||
        headerNamesZone(source.timeZone, displayZone!, startTime, browserZone) ||
        headerNamesZone(source.timeZone, displayZone!, last, browserZone));
    const year = new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone });
    // The year is written only when the two ends fall in different years, so a year-long event can't read as one day.
    const withYear = ends && year.format(endTime) !== year.format(startTime);
    const day = new Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
      year: withYear ? 'numeric' : undefined,
      timeZone,
    });
    const clock = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone });
    const at = (instant: Date) => `${day.format(instant)}, ${clock.format(instant)}`;
    const startName = named ? zoneShortName(displayZone!, startTime) : '';
    const endName = named ? zoneShortName(displayZone!, last) : '';
    // Across a daylight-saving change each end carries its own name ("1:00 AM EDT – 1:30 AM EST").
    let text = at(startTime) + (startName && endName !== startName ? ` ${startName}` : '');
    if (ends) {
      text += ` – ${day.format(endTime) === day.format(startTime) ? clock.format(endTime) : at(endTime)}`;
    }
    parts.push(endName ? `${text} ${endName}` : text);
  }

  if (recurrence) {
    // The end date as the Calendar card dates it, so header and card name the same last day.
    const label = recurrenceLabel(recurrence, startTime, { allDay, timeZone: source.timeZone });
    parts.push(`Repeats ${label.charAt(0).toLowerCase()}${label.slice(1)}`);
  }
  return parts.join(' · ');
}
